import React, { useState, useRef, useCallback } from 'react';
import VideoStreamPanel from '../components/faceai/VideoStreamPanel';
import { useCreateEntityFromScan } from '../api/entities';
import {
  useFaceAIStatus,
  useFaceAIStats,
  useFaceAIUsers,
  useFaceAIHistory,
  useDetectFaces,
  useRecognizeFaces,
  useEnrollFace,
  useCompareFaces,
  useSearchFace,
  useAnalyzeFace,
  useDeleteFaceAIUser,
  useOsintAnalyze,
  useReverseImageSearch,
  type DetectResponse,
  type RecognizeResponse,
  type EnrollResponse,
  type CompareResponse,
  type SearchResponse,
  type AnalyzeResponse,
  type OsintAnalyzeResponse,
  type ReverseImageSearchResponse,
} from '../api/faceai';

// ── Shared helpers & sub-components ──────────────────────────────────────────

function formatMs(ms: number): string {
  return ms < 1000 ? `${ms.toFixed(0)}ms` : `${(ms / 1000).toFixed(2)}s`;
}

function StatusDot({ active }: { active: boolean }) {
  return (
    <div
      className={`w-2.5 h-2.5 rounded-full ${
        active ? 'bg-green-500 animate-pulse shadow-green-500/50' : 'bg-red-500 shadow-red-500/50'
      } shadow-lg`}
    />
  );
}

function Spinner({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function KPI({ label, value, color = 'cyan' }: { label: string; value: string | number; color?: string }) {
  const colors: Record<string, string> = {
    cyan: 'border-cyan-500/30 text-cyan-400',
    purple: 'border-purple-500/30 text-purple-400',
    green: 'border-green-500/30 text-green-400',
    amber: 'border-amber-500/30 text-amber-400',
    blue: 'border-blue-500/30 text-blue-400',
    emerald: 'border-emerald-500/30 text-emerald-400',
    red: 'border-red-500/30 text-red-400',
    pink: 'border-pink-500/30 text-pink-400',
  };
  return (
    <div className={`bg-surface-secondary rounded-xl border p-4 ${colors[color] || colors.cyan}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-bold mt-1">{String(value)}</div>
    </div>
  );
}

function EmotionBars({ emotions }: { emotions: Record<string, number> }) {
  const EMOTION_COLORS: Record<string, string> = {
    happy: 'bg-yellow-400',
    sad: 'bg-blue-400',
    angry: 'bg-red-400',
    fear: 'bg-purple-400',
    surprise: 'bg-orange-400',
    disgust: 'bg-green-400',
    neutral: 'bg-gray-400',
  };
  const sorted = Object.entries(emotions).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-1.5">
      {sorted.map(([emotion, score]) => (
        <div key={emotion} className="flex items-center gap-2">
          <span className="text-xs text-gray-400 w-16 capitalize">{emotion}</span>
          <div className="flex-1 h-2 bg-surface-tertiary rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${EMOTION_COLORS[emotion] || 'bg-gray-400'}`}
              style={{ width: `${Math.max(1, score * 100)}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 w-10 text-right font-mono">{(score * 100).toFixed(0)}%</span>
        </div>
      ))}
    </div>
  );
}

function QualityGauge({ value }: { value: number }) {
  const pct = value * 100;
  const color = pct >= 70 ? 'text-green-400' : pct >= 40 ? 'text-amber-400' : 'text-red-400';
  const bg = pct >= 70 ? 'bg-green-400' : pct >= 40 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-3 bg-surface-tertiary rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${bg}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-sm font-bold ${color}`}>{pct.toFixed(0)}%</span>
    </div>
  );
}

function ConfidenceBadge({ value }: { value: number }) {
  const pct = value * 100;
  const c =
    pct >= 90
      ? 'text-green-400 bg-green-500/15 border-green-500/30'
      : pct >= 70
        ? 'text-yellow-400 bg-yellow-500/15 border-yellow-500/30'
        : 'text-red-400 bg-red-500/15 border-red-500/30';
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${c}`}>{pct.toFixed(1)}%</span>;
}

function ErrorMsg({ error }: { error: unknown }) {
  return (
    <p className="mt-2 text-xs text-red-400">
      Error: {error instanceof Error ? error.message : 'Unknown error'}
    </p>
  );
}

function ImageDropZone({
  id,
  file,
  onFileChange,
  label,
  compact,
}: {
  id: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  label?: string;
  compact?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files[0];
      if (f?.type.startsWith('image/')) onFileChange(f);
    },
    [onFileChange],
  );

  const preview = file ? URL.createObjectURL(file) : null;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`relative cursor-pointer rounded-lg border-2 border-dashed transition-all flex flex-col items-center justify-center ${
        compact ? 'p-3 min-h-[100px]' : 'p-4 min-h-[140px]'
      } ${
        dragOver
          ? 'border-cyber-cyan bg-cyber-cyan/5'
          : file
            ? 'border-border-subtle bg-surface-tertiary'
            : 'border-border-subtle hover:border-gray-500 bg-surface-tertiary'
      }`}
    >
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
      />
      {preview ? (
        <div className="flex flex-col items-center gap-1.5">
          <img src={preview} alt="Preview" className={`rounded object-contain ${compact ? 'max-h-16' : 'max-h-24'}`} />
          <span className="text-xs text-gray-400 truncate max-w-[160px]">{file!.name}</span>
          <button
            onClick={(e) => { e.stopPropagation(); onFileChange(null); }}
            className="text-xs text-red-400 hover:text-red-300"
          >
            Remove
          </button>
        </div>
      ) : (
        <>
          <svg className="w-7 h-7 text-gray-500 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
          </svg>
          <span className="text-xs text-gray-500">{label || 'Drop image or click to browse'}</span>
        </>
      )}
    </div>
  );
}

const BTN =
  'w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2';

type Tab = 'identity' | 'video' | 'detect' | 'analyze' | 'recognize' | 'enroll' | 'compare' | 'search' | 'osint' | 'history' | 'gallery';

const TABS: { key: Tab; label: string }[] = [
  { key: 'identity', label: 'Full Identity' },
  { key: 'video', label: 'Video Stream' },
  { key: 'detect', label: 'Detection' },
  { key: 'analyze', label: 'Analyse' },
  { key: 'recognize', label: 'Recognition' },
  { key: 'enroll', label: 'Enrollment' },
  { key: 'compare', label: 'Comparison' },
  { key: 'search', label: 'Search' },
  { key: 'osint', label: 'OSINT' },
  { key: 'history', label: 'Historique' },
  { key: 'gallery', label: 'Gallery' },
];

// ── Main Component ──────────────────────────────────────────────────────────

export default function BiometricsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('identity');

  const { data: status } = useFaceAIStatus();
  const { data: stats } = useFaceAIStats();
  const isActive = status?.status === 'active';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Face AI Lab</h1>
          <p className="text-sm text-gray-500 mt-1">
            Biometric analysis, recognition, OSINT & identity management
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusDot active={isActive} />
          <span className="text-sm text-gray-300 capitalize">{status?.status ?? 'Loading...'}</span>
        </div>
      </div>

      {/* System Info Bar */}
      <div className="bg-surface-secondary rounded-xl border border-border-subtle p-4 flex flex-wrap items-center gap-5 text-xs text-gray-400">
        <div><span className="text-gray-500">Backend:</span> <span className="text-gray-200">{status?.detector_backend ?? '--'}</span></div>
        <div className="h-3 w-px bg-border-subtle" />
        <div><span className="text-gray-500">Encoder:</span> <span className="text-gray-200">{status?.encoder_model ?? '--'}</span></div>
        <div className="h-3 w-px bg-border-subtle" />
        <div><span className="text-gray-500">Gallery:</span> <span className="text-cyber-cyan font-semibold">{status?.gallery_size ?? 0}</span></div>
        <div className="h-3 w-px bg-border-subtle" />
        <div><span className="text-gray-500">FAISS Index:</span> <span className="text-gray-200">{stats?.index?.total_vectors ?? 0} vectors ({stats?.index?.dimension ?? 512}d)</span></div>
        <div className="h-3 w-px bg-border-subtle" />
        <div><span className="text-gray-500">Comparator:</span> <span className={status?.biometric_comparator ? 'text-green-400' : 'text-gray-500'}>{status?.biometric_comparator ? 'Active' : 'Off'}</span></div>
        {status?.capabilities && (
          <>
            <div className="h-3 w-px bg-border-subtle" />
            <div className="flex flex-wrap gap-1">
              {status.capabilities.map((c) => (
                <span key={c} className="px-1.5 py-0.5 rounded bg-helios-700/30 text-cyber-cyan/70 border border-cyber-cyan/10">{c.replace(/_/g, ' ')}</span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <KPI label="Enrolled" value={stats?.users ?? 0} color="cyan" />
        <KPI label="Recognitions" value={stats?.recognitions ?? 0} color="purple" />
        <KPI label="Searches" value={stats?.searches ?? 0} color="blue" />
        <KPI label="Gallery Size" value={stats?.gallery_size ?? 0} color="green" />
        <KPI label="OSINT Sources" value={stats?.osint_sources ?? 0} color="amber" />
        <KPI label="OSINT Entities" value={stats?.osint_entities ?? 0} color="pink" />
        <KPI label="Active Alerts" value={stats?.active_alerts ?? 0} color="red" />
        <KPI label="FAISS Vectors" value={stats?.index?.total_vectors ?? 0} color="emerald" />
      </div>

      {/* Tab Navigation */}
      <div className="bg-surface-secondary rounded-xl border border-border-subtle">
        <div className="flex overflow-x-auto border-b border-border-subtle">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'text-cyber-cyan border-b-2 border-cyber-cyan'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'identity' && <FullIdentitySection />}
          {activeTab === 'video' && <VideoStreamSection />}
          {activeTab === 'detect' && <DetectSection />}
          {activeTab === 'analyze' && <AnalyzeSection />}
          {activeTab === 'recognize' && <RecognizeSection />}
          {activeTab === 'enroll' && <EnrollSection />}
          {activeTab === 'compare' && <CompareSection />}
          {activeTab === 'search' && <SearchSection />}
          {activeTab === 'osint' && <OsintSection />}
          {activeTab === 'history' && <HistorySection />}
          {activeTab === 'gallery' && <GallerySection />}
        </div>
      </div>
    </div>
  );
}

// ── Video Stream Section ────────────────────────────────────────────────────

function VideoStreamSection() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-100">Video Stream Recognition</h3>
          <p className="text-xs text-gray-500">Reconnaissance faciale en temps reel via webcam ou flux video</p>
        </div>
      </div>
      <VideoStreamPanel />
    </div>
  );
}

// ── Full Identity Pipeline ──────────────────────────────────────────────────

function FullIdentitySection() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [entitySaved, setEntitySaved] = useState(false);

  // 3 parallel pipelines
  const osintAnalyze = useOsintAnalyze();
  const reverseSearch = useReverseImageSearch();
  const detectFaces = useDetectFaces();
  const saveEntityMut = useCreateEntityFromScan();

  function handleFile(f: File) {
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setEntitySaved(false);
    // Reset previous results
    osintAnalyze.reset();
    reverseSearch.reset();
    detectFaces.reset();
    saveEntityMut.reset();
  }

  function handleSaveEntity() {
    const name = reverse?.entity_name || reverse?.best_guess || osint?.osint_metadata?.name as string || '';
    if (!name) return;
    const identity = (reverse?.identity ?? {}) as Record<string, unknown>;
    const enrichment = ((reverse as unknown as Record<string, unknown>)?.enrichment ?? {}) as Record<string, unknown>;

    saveEntityMut.mutate({
      name,
      identity,
      social_profiles: (identity.social_profiles ?? {}) as Record<string, unknown>,
      digital_life: (reverse?.digital_life ?? {}) as Record<string, unknown>,
      enrichment,
      entourage: (identity.entourage ?? {}) as Record<string, unknown>,
      photos: ((identity.images ?? []) as string[]),
      best_guess: reverse?.best_guess || '',
      source: 'faceai_lab',
    }, {
      onSuccess: () => setEntitySaved(true),
    });
  }

  function handleFullScan() {
    if (!file) return;
    // Launch all 3 in parallel
    osintAnalyze.mutate(file);
    reverseSearch.mutate(file);
    detectFaces.mutate(file);
  }

  const osint = osintAnalyze.data as (OsintAnalyzeResponse & { reverse_search?: ReverseImageSearchResponse }) | undefined;
  const reverse = reverseSearch.data;
  const detect = detectFaces.data;
  const isLoading = osintAnalyze.isPending || reverseSearch.isPending || detectFaces.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-helios-800/60 to-helios-700/40 border border-cyber-cyan/20 rounded-xl p-5">
        <h3 className="text-lg font-bold text-gray-100 flex items-center gap-2">
          <svg className="w-5 h-5 text-cyber-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
          Full Identity Pipeline
        </h3>
        <p className="text-xs text-gray-400 mt-1">
          Chargez une photo → Détection faciale + Analyse biométrique + Reverse Image Search (Google, Yandex, Bing, TinEye) + OSINT Cross-Referencing automatique
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          {['ArcFace 512D', 'Anti-Spoofing', 'FAISS Index', '5 Moteurs de Recherche', '19 Réseaux Sociaux', 'EXIF/OCR', 'Analyse Morphologique'].map((cap) => (
            <span key={cap} className="text-[10px] px-2 py-0.5 bg-cyber-cyan/10 text-cyber-cyan/80 border border-cyber-cyan/20 rounded">{cap}</span>
          ))}
        </div>
      </div>

      {/* Upload + Scan */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
          <button
            onClick={() => fileRef.current?.click()}
            className="w-full aspect-square rounded-xl border-2 border-dashed border-border-subtle hover:border-cyber-cyan/40 bg-surface-tertiary flex flex-col items-center justify-center gap-3 transition-all overflow-hidden group"
          >
            {preview ? (
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <>
                <svg className="w-12 h-12 text-gray-600 group-hover:text-cyber-cyan/60 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21z" />
                </svg>
                <span className="text-xs text-gray-500">Charger une photo</span>
              </>
            )}
          </button>
          <button onClick={handleFullScan} disabled={!file || isLoading} className={`${BTN} mt-3`}>
            {isLoading ? <><Spinner /> Scan en cours...</> : 'Lancer Full Identity Scan'}
          </button>

          {/* Architecture */}
          <div className="mt-4 bg-surface-tertiary rounded-lg border border-border-subtle p-3">
            <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Architecture</h4>
            <div className="space-y-1 text-[10px] text-gray-500">
              <div className="flex items-center gap-1"><span className="text-cyber-cyan">1.</span> Détection multi-backend (RetinaFace → DeepFace → OpenCV DNN)</div>
              <div className="flex items-center gap-1"><span className="text-cyber-cyan">2.</span> Embedding ArcFace 512D (L2 normalisé, cosine similarity)</div>
              <div className="flex items-center gap-1"><span className="text-cyber-cyan">3.</span> Anti-spoofing 4 critères (texture LBP, fréquence FFT, couleur YCbCr, moiré)</div>
              <div className="flex items-center gap-1"><span className="text-cyber-cyan">4.</span> Comparaison FAISS (IndexFlatIP / IVFFlat / IVFPQ)</div>
              <div className="flex items-center gap-1"><span className="text-cyber-cyan">5.</span> Reverse Image Search (Google Lens, Yandex, Bing, TinEye)</div>
              <div className="flex items-center gap-1"><span className="text-cyber-cyan">6.</span> OSINT: EXIF, OCR, métadonnées, Wikipedia, réseaux sociaux</div>
              <div className="flex items-center gap-1"><span className="text-cyber-cyan">7.</span> Triplet Loss training: rapproche les embeddings de même personne</div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-2 space-y-4">
          {/* Face Detection Results */}
          {detect && (
            <div className="bg-surface-tertiary rounded-xl border border-border-subtle p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-200">Détection & Analyse Biométrique</h4>
                <span className="text-xs text-gray-500">{detect.faces_detected} visage(s) • {formatMs(detect.processing_time_ms)}</span>
              </div>
              <div className="space-y-3">
                {(detect.results ?? []).map((face, i) => (
                  <div key={i} className="bg-surface-primary rounded-lg p-3 border border-border-subtle">
                    <div className="flex items-center gap-3 mb-2">
                      {face.face_thumbnail && (
                        <img src={`data:image/jpeg;base64,${face.face_thumbnail}`} alt="Face" className="w-16 h-16 rounded-lg object-cover border border-cyber-cyan/30" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-200">Visage #{i + 1}</span>
                          <ConfidenceBadge value={face.confidence} />
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          BBox: [{face.bbox.map((b) => b.toFixed(0)).join(', ')}]
                        </div>
                      </div>
                    </div>
                    {face.analysis && Object.keys(face.analysis).length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                        {face.analysis.age != null && (
                          <div className="text-center p-2 bg-surface-tertiary rounded">
                            <div className="text-lg font-bold text-cyber-cyan">{String(face.analysis.age)}</div>
                            <div className="text-[10px] text-gray-500">Age estimé</div>
                          </div>
                        )}
                        {face.analysis.gender != null && (
                          <div className="text-center p-2 bg-surface-tertiary rounded">
                            <div className="text-sm font-bold text-gray-200">{String(face.analysis.gender)}</div>
                            <div className="text-[10px] text-gray-500">Genre ({((face.analysis.gender_confidence as number) * 100).toFixed(0)}%)</div>
                          </div>
                        )}
                        {face.analysis.dominant_emotion != null && (
                          <div className="text-center p-2 bg-surface-tertiary rounded">
                            <div className="text-sm font-bold text-amber-400 capitalize">{String(face.analysis.dominant_emotion)}</div>
                            <div className="text-[10px] text-gray-500">Emotion</div>
                          </div>
                        )}
                        {face.analysis.face_quality != null && (
                          <div className="text-center p-2 bg-surface-tertiary rounded">
                            <div className="text-lg font-bold text-green-400">{((face.analysis.face_quality as number) * 100).toFixed(0)}%</div>
                            <div className="text-[10px] text-gray-500">Qualité</div>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Emotion bars */}
                    {!!face.analysis?.emotions && (
                      <div className="mt-2">
                        <div className="text-[10px] text-gray-500 mb-1">Distribution des émotions</div>
                        <div className="space-y-0.5">
                          {Object.entries(face.analysis.emotions as Record<string, number>)
                            .sort(([, a], [, b]) => b - a)
                            .map(([emotion, value]) => (
                              <div key={emotion} className="flex items-center gap-2">
                                <span className="text-[10px] text-gray-400 w-16 text-right capitalize">{emotion}</span>
                                <div className="flex-1 h-1.5 bg-surface-primary rounded-full overflow-hidden">
                                  <div className="h-full bg-gradient-to-r from-cyber-cyan to-helios-500 rounded-full" style={{ width: `${Math.min(100, value * 100)}%` }} />
                                </div>
                                <span className="text-[10px] text-gray-500 w-10">{(value * 100).toFixed(0)}%</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    {/* Gaze + Landmarks */}
                    <div className="flex gap-4 mt-2 text-[10px] text-gray-500">
                      {!!face.analysis?.gaze_direction && <span>Direction regard: <span className="text-gray-300">{String(face.analysis.gaze_direction)}</span></span>}
                      {!!face.analysis?.face_shape && <span>Forme: <span className="text-gray-300">{String(face.analysis.face_shape)}</span></span>}
                      {!!face.analysis?.orientation && <span>Orientation: <span className="text-gray-300">{String(face.analysis.orientation)}</span></span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* OSINT Analysis */}
          {osint && (
            <div className="bg-surface-tertiary rounded-xl border border-border-subtle p-4">
              <h4 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
                Analyse OSINT Image
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div className="text-center p-2 bg-surface-primary rounded">
                  <div className="text-lg font-bold text-cyber-cyan">{osint.faces_count ?? 0}</div>
                  <div className="text-[10px] text-gray-500">Visages</div>
                </div>
                <div className="text-center p-2 bg-surface-primary rounded">
                  <div className="text-lg font-bold text-purple-400">{formatMs(osint.processing_time_ms)}</div>
                  <div className="text-[10px] text-gray-500">Temps</div>
                </div>
                <div className="text-center p-2 bg-surface-primary rounded">
                  <div className="text-lg font-bold text-green-400">{(osint.faces ?? []).filter((f) => f.has_encoding).length}</div>
                  <div className="text-[10px] text-gray-500">Encodings</div>
                </div>
                <div className="text-center p-2 bg-surface-primary rounded">
                  <div className="text-lg font-bold text-amber-400">{Object.keys(osint.osint_metadata ?? {}).length}</div>
                  <div className="text-[10px] text-gray-500">Métadonnées</div>
                </div>
              </div>
              {/* OSINT Metadata */}
              {Object.keys(osint.osint_metadata ?? {}).length > 0 && (
                <div className="bg-surface-primary rounded-lg p-3 border border-border-subtle">
                  <h5 className="text-[10px] text-gray-400 font-semibold uppercase mb-2">Métadonnées OSINT</h5>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(osint.osint_metadata ?? {}).map(([key, val]) => (
                      <div key={key} className="text-[10px]">
                        <span className="text-gray-500">{key}: </span>
                        <span className="text-gray-300">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reverse Image Search Results */}
          {reverse && (
            <div className="bg-surface-tertiary rounded-xl border border-cyber-cyan/20 p-4">
              <h4 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-cyber-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
                Reverse Image Search — Sources & Liens
              </h4>

              {/* Identity Header */}
              {(reverse.entity_name || reverse.best_guess) && (
                <div className="bg-gradient-to-r from-helios-800/60 to-transparent border border-cyber-cyan/20 rounded-lg p-4 mb-4">
                  <div className="text-lg font-bold text-cyber-cyan">{reverse.entity_name || reverse.best_guess}</div>
                  <div className="text-xs text-gray-400 mt-1">Identité principale détectée par les moteurs de recherche</div>
                  {!!(reverse.identity && Object.keys(reverse.identity).length > 0) && ((): React.ReactNode => {
                    const labelMap: Record<string, string> = {
                      name: 'Nom complet', first_name: 'Prénom', last_name: 'Nom', function: 'Fonction',
                      bio: 'Biographie', organization: 'Organisation', location: 'Localisation',
                      country: 'Pays', city: 'Ville', address: 'Adresse', phone: 'Téléphone',
                      email: 'Email', birth_date: 'Date de naissance', nationality: 'Nationalité',
                      confidence: 'Score de confiance', social_profiles: 'Réseaux sociaux',
                      images: 'Photos trouvées', sources: 'Sources', confidence_details: 'Détails du score',
                      entourage: 'Entourage',
                    };
                    const identity = reverse.identity as Record<string, unknown>;
                    const simpleFields = Object.entries(identity).filter(
                      ([k]) => !['social_profiles', 'images', 'sources', 'confidence_details', 'entourage'].includes(k)
                    );
                    const socialProfiles = identity.social_profiles as Record<string, string | string[]> | Array<{ platform?: string; url?: string }> | undefined;
                    const sources = identity.sources as Array<{ title?: string; url?: string; domain?: string; description?: string }> | undefined;
                    const images = identity.images as string[] | undefined;
                    const confidenceDetails = identity.confidence_details as Record<string, unknown> | undefined;
                    const entourage = identity.entourage as Record<string, Array<{ name?: string; relation?: string }>> | string[] | Array<{ name?: string }> | undefined;

                    return (
                      <div className="mt-3 space-y-3">
                        {/* Simple text fields */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          {simpleFields.map(([key, val]) => {
                            if (val === null || val === undefined || val === '' || (typeof val === 'object')) return null;
                            const display = key === 'confidence' ? `${(Number(val) * 100).toFixed(0)}%` : String(val);
                            return (
                              <div key={key} className="text-xs">
                                <span className="text-gray-500">{labelMap[key] ?? key}: </span>
                                <span className="text-gray-300">{display}</span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Social Profiles */}
                        {socialProfiles && (Array.isArray(socialProfiles) ? socialProfiles.length > 0 : Object.keys(socialProfiles).length > 0) && (
                          <div>
                            <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Réseaux sociaux</div>
                            <div className="space-y-2">
                              {Array.isArray(socialProfiles)
                                ? socialProfiles.map((sp, i) => (
                                    <a key={i} href={sp.url ?? '#'} target="_blank" rel="noopener noreferrer"
                                       className="text-[10px] px-2 py-1 bg-cyber-cyan/10 text-cyber-cyan border border-cyber-cyan/20 rounded-full hover:bg-cyber-cyan/20 transition-colors">
                                      {sp.platform ?? `Profil ${i + 1}`}
                                    </a>
                                  ))
                                : Object.entries(socialProfiles).map(([platform, urls]) => {
                                    // urls can be a string or string[] — normalize
                                    const urlList = Array.isArray(urls) ? urls : [String(urls)];
                                    // Filter to keep only real profile URLs (not share/intent links)
                                    const profileUrls = urlList.filter(u =>
                                      u.startsWith('http') && !u.includes('/sharer') && !u.includes('/share?')
                                      && !u.includes('intent/tweet') && !u.includes('shareArticle')
                                    ).slice(0, 3);
                                    if (profileUrls.length === 0) return null;
                                    return (
                                      <div key={platform}>
                                        <span className="text-[10px] text-gray-400 capitalize font-medium">{platform}</span>
                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                          {profileUrls.map((url, j) => (
                                            <a key={j} href={url} target="_blank" rel="noopener noreferrer"
                                               className="text-[10px] px-2 py-0.5 bg-cyber-cyan/10 text-cyber-cyan border border-cyber-cyan/20 rounded-full hover:bg-cyber-cyan/20 transition-colors truncate max-w-[250px]">
                                              {new URL(url).pathname.split('/').filter(Boolean).pop() || platform}
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })
                              }
                            </div>
                          </div>
                        )}

                        {/* Sources */}
                        {sources && sources.length > 0 && (
                          <div>
                            <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Sources</div>
                            <div className="space-y-1">
                              {sources.map((src, i) => (
                                <a key={i} href={src.url ?? '#'} target="_blank" rel="noopener noreferrer"
                                   className="block text-xs bg-surface-primary rounded p-2 border border-border-subtle hover:border-cyber-cyan/30 transition-colors">
                                  <span className="text-gray-200">{src.title ?? src.domain ?? src.url ?? 'Source'}</span>
                                  {src.url && <span className="block text-[10px] text-blue-400/70 truncate mt-0.5">{src.url}</span>}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Images */}
                        {images && images.length > 0 && (
                          <div>
                            <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Photos trouvées</div>
                            <div className="flex gap-2 flex-wrap">
                              {images.map((img, i) => (
                                <a key={i} href={img} target="_blank" rel="noopener noreferrer">
                                  <img src={img} alt={`Photo ${i + 1}`} className="w-16 h-16 rounded border border-border-subtle object-cover hover:border-cyber-cyan/40 transition-colors" />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Confidence Details */}
                        {confidenceDetails && Object.keys(confidenceDetails).length > 0 && (
                          <div>
                            <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Détails du score</div>
                            <div className="grid grid-cols-2 gap-1">
                              {Object.entries(confidenceDetails).map(([k, v]) => (
                                <div key={k} className="text-[10px]">
                                  <span className="text-gray-500">{k}: </span>
                                  <span className="text-gray-300">{typeof v === 'number' ? `${(v * 100).toFixed(0)}%` : String(v)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Entourage */}
                        {entourage ? ((): React.ReactNode => {
                          // entourage can be: {family: [{name, relation}], associates: [...]} or string[] or [{name}]
                          if (typeof entourage === 'object' && !Array.isArray(entourage)) {
                            const sections = entourage as Record<string, Array<{ name?: string; relation?: string }>>;
                            const allPeople = Object.entries(sections)
                              .flatMap(([cat, people]) => (people ?? []).map(p => ({ ...p, category: cat })));
                            if (allPeople.length === 0) return null;
                            return (
                              <div>
                                <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Entourage</div>
                                <div className="flex flex-wrap gap-1">
                                  {allPeople.map((person, i) => (
                                    <span key={i} className="text-[10px] px-2 py-0.5 bg-surface-primary text-gray-300 border border-border-subtle rounded-full">
                                      {person.name ?? '?'}{person.relation ? ` (${person.relation})` : ''}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          const arr = entourage as unknown[];
                          if (arr.length === 0) return null;
                          return (
                            <div>
                              <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Entourage</div>
                              <div className="flex flex-wrap gap-1">
                                {arr.map((person, i) => (
                                  <span key={i} className="text-[10px] px-2 py-0.5 bg-surface-primary text-gray-300 border border-border-subtle rounded-full">
                                    {typeof person === 'string' ? person : (person as { name?: string }).name ?? '?'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })() : null}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Engines Status */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {(reverse.engines_searched ?? []).map((e) => (
                  <span key={e} className="text-[10px] px-2 py-0.5 bg-green-500/20 text-green-400 border border-green-500/20 rounded-full">{e}</span>
                ))}
                {(reverse.engines_failed ?? []).map((e) => (
                  <span key={e} className="text-[10px] px-2 py-0.5 bg-red-500/20 text-red-400 border border-red-500/20 rounded-full">{e} (failed)</span>
                ))}
                <span className="text-[10px] text-gray-500 self-center ml-auto">{reverse.total_results ?? 0} résultats • {((reverse.search_time_ms ?? 0) / 1000).toFixed(1)}s</span>
              </div>

              {/* OSINT Enrichment (Maigret, Holehe, Username checks) */}
              {(reverse as unknown as Record<string, unknown>).enrichment && ((): React.ReactNode => {
                const enrichment = (reverse as unknown as Record<string, unknown>).enrichment as Record<string, unknown>;
                const usernames = (enrichment.usernames_found ?? []) as Array<{ platform: string; url: string; username: string; category: string }>;
                const emailAccounts = (enrichment.email_accounts ?? []) as Array<{ platform: string; exists: boolean; url: string }>;
                const additionalProfiles = (enrichment.additional_profiles ?? []) as Array<{ platform?: string; url: string; title?: string; description?: string; type?: string }>;
                const totalAccounts = (enrichment.total_accounts as number) ?? 0;
                const toolsUsed = (enrichment.tools_used ?? []) as string[];

                if (totalAccounts === 0 && additionalProfiles.length === 0) return null;

                // Group usernames by category
                const byCategory: Record<string, typeof usernames> = {};
                for (const u of usernames) {
                  const cat = u.category || 'other';
                  if (!byCategory[cat]) byCategory[cat] = [];
                  byCategory[cat].push(u);
                }
                const catLabels: Record<string, string> = {
                  social: 'Réseaux sociaux', tech: 'Tech & Dev', media: 'Médias & Créatif', other: 'Autres'
                };

                return (
                  <div className="mb-4 bg-surface-primary/50 rounded-lg p-3 border border-cyber-cyan/10">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-xs font-semibold text-cyber-cyan">Comptes trouvés ({totalAccounts})</h5>
                      <div className="flex gap-1">
                        {toolsUsed.map(t => (
                          <span key={t} className="text-[9px] px-1.5 py-0.5 bg-cyber-cyan/10 text-cyber-cyan/60 rounded">{t}</span>
                        ))}
                      </div>
                    </div>

                    {/* Username results by category */}
                    {Object.entries(byCategory).map(([cat, items]) => (
                      <div key={cat} className="mb-2">
                        <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">{catLabels[cat] ?? cat}</div>
                        <div className="flex flex-wrap gap-1">
                          {items.map((u, i) => (
                            <a key={i} href={u.url} target="_blank" rel="noopener noreferrer"
                               className="text-[10px] px-2 py-0.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded-full hover:bg-green-500/20 transition-colors">
                              {u.platform}
                            </a>
                          ))}
                        </div>
                      </div>
                    ))}

                    {/* Email accounts from Holehe */}
                    {emailAccounts.length > 0 && (
                      <div className="mb-2">
                        <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Comptes email associés</div>
                        <div className="flex flex-wrap gap-1">
                          {emailAccounts.map((e, i) => (
                            <a key={i} href={e.url || '#'} target="_blank" rel="noopener noreferrer"
                               className="text-[10px] px-2 py-0.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-full hover:bg-yellow-500/20 transition-colors">
                              {e.platform}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Additional profiles from DDG search */}
                    {additionalProfiles.length > 0 && (
                      <div>
                        <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">Profils & mentions web</div>
                        <div className="space-y-1">
                          {additionalProfiles.slice(0, 10).map((p, i) => (
                            <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
                               className="block text-xs bg-surface-primary rounded p-1.5 border border-border-subtle hover:border-cyber-cyan/30 transition-colors">
                              <span className="text-gray-200">{p.title ?? p.platform ?? 'Profil'}</span>
                              {p.description && <span className="block text-[10px] text-gray-500 truncate mt-0.5">{p.description}</span>}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Digital Life (Social Media, News, Articles) */}
              {reverse.digital_life && Object.keys(reverse.digital_life).length > 0 && (() => {
                const dl = reverse.digital_life as Record<string, unknown>;
                const categoryLabels: Record<string, string> = {
                  social_media: 'Réseaux Sociaux', news: 'Actualités', articles: 'Articles',
                  interviews: 'Interviews', videos: 'Vidéos', podcasts: 'Podcasts',
                  publications: 'Publications', mentions: 'Mentions',
                };
                return (
                  <div className="mb-4 space-y-3">
                    <h5 className="text-xs font-semibold text-gray-400 mb-2">Vie Numérique</h5>
                    {Object.entries(dl).map(([category, items]) => {
                      if (!items || (Array.isArray(items) && items.length === 0)) return null;
                      const itemList = Array.isArray(items) ? items : typeof items === 'object' && items !== null ? [items] : [];
                      if (itemList.length === 0 && typeof items === 'string') {
                        return (
                          <div key={category} className="text-xs">
                            <span className="text-gray-500">{categoryLabels[category] ?? category}: </span>
                            <span className="text-gray-300">{items}</span>
                          </div>
                        );
                      }
                      return (
                        <div key={category}>
                          <div className="text-[10px] text-gray-500 uppercase font-semibold mb-1">{categoryLabels[category] ?? category}</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {itemList.map((item, i) => {
                              const obj = typeof item === 'object' && item !== null ? item as Record<string, unknown> : null;
                              const url = obj?.url ? String(obj.url) : typeof item === 'string' ? item : '#';
                              const title = obj?.title ? String(obj.title) : obj?.platform ? String(obj.platform) : obj?.name ? String(obj.name) : typeof item === 'string' ? item : category;
                              const subtitle = obj?.description ? String(obj.description) : obj?.username ? `@${String(obj.username)}` : obj?.domain ? String(obj.domain) : url !== '#' ? url : '';
                              return (
                                <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                                   className="bg-surface-primary rounded-lg p-2 border border-border-subtle hover:border-cyber-cyan/30 transition-all group">
                                  <div className="text-xs font-medium text-gray-200 group-hover:text-cyber-cyan transition-colors line-clamp-1">{title}</div>
                                  {subtitle && <div className="text-[10px] text-gray-500 truncate mt-0.5">{subtitle}</div>}
                                  {obj?.date ? <div className="text-[10px] text-gray-600 mt-0.5">{String(obj.date)}</div> : null}
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Search Results with Links */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {(reverse.results ?? []).map((r, i) => (
                  <a
                    key={i}
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-surface-primary rounded-lg p-3 border border-border-subtle hover:border-cyber-cyan/30 transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-200 group-hover:text-cyber-cyan transition-colors line-clamp-1">{r.title}</span>
                      <span className="text-[10px] text-gray-500 flex-shrink-0 ml-2">{r.source}</span>
                    </div>
                    <div className="text-[10px] text-blue-400/80 truncate mt-0.5">{r.url}</div>
                    {r.confidence != null && r.confidence > 0 && (
                      <div className="text-[10px] text-gray-500 mt-0.5">Similarité: {(r.confidence * 100).toFixed(0)}%</div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Save as Entity Button */}
          {!isLoading && (reverse?.entity_name || reverse?.best_guess) && (
            <div className="bg-surface-tertiary rounded-xl border border-border-subtle p-4">
              {entitySaved || saveEntityMut.isSuccess ? (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <div className="text-sm font-medium text-green-400">Entit{'\u00e9'} sauvegard{'\u00e9'}e</div>
                    <div className="text-[10px] text-gray-500">
                      {saveEntityMut.data?.total_entities ?? 0} entit{'\u00e9'}s + {saveEntityMut.data?.total_relationships ?? 0} relations cr{'\u00e9'}{'\u00e9'}es.
                      Visible dans Entity Explorer et Graph Explorer.
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleSaveEntity}
                  disabled={saveEntityMut.isPending}
                  className="w-full px-4 py-3 text-sm font-medium bg-gradient-to-r from-green-500/20 to-helios-600/20 text-green-400 border border-green-500/30 rounded-lg hover:from-green-500/30 hover:to-helios-600/30 transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {saveEntityMut.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin" />
                      Sauvegarde en cours...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Sauvegarder comme Entit{'\u00e9'} — {reverse?.entity_name || reverse?.best_guess}
                    </>
                  )}
                </button>
              )}
              {saveEntityMut.error && (
                <div className="mt-2 text-xs text-red-400">Erreur: {String(saveEntityMut.error)}</div>
              )}
            </div>
          )}

          {/* Loading States */}
          {isLoading && (
            <div className="bg-surface-tertiary rounded-xl border border-border-subtle p-8 text-center">
              <Spinner className="w-8 h-8 text-cyber-cyan mx-auto" />
              <div className="text-sm text-gray-400 mt-3">
                {osintAnalyze.isPending && 'Analyse OSINT en cours...'}
                {reverseSearch.isPending && ' Recherche inversée d\'image (Google, Yandex, Bing, TinEye)...'}
                {detectFaces.isPending && ' Détection faciale et encodage ArcFace 512D...'}
              </div>
              <div className="text-[10px] text-gray-600 mt-1">
                Le visage est converti en vecteur mathématique 512D via ArcFace (triplet loss) et comparé par distance cosinus
              </div>
            </div>
          )}

          {/* Error States */}
          {(osintAnalyze.error || reverseSearch.error || detectFaces.error) && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              {osintAnalyze.error && <div className="text-xs text-red-400">OSINT: {String(osintAnalyze.error)}</div>}
              {reverseSearch.error && <div className="text-xs text-red-400">Reverse Search: {String(reverseSearch.error)}</div>}
              {detectFaces.error && <div className="text-xs text-red-400">Detection: {String(detectFaces.error)}</div>}
            </div>
          )}

          {/* No file selected */}
          {!file && !isLoading && (
            <div className="bg-surface-tertiary rounded-xl border border-border-subtle p-12 text-center">
              <div className="text-gray-500 text-sm mb-2">Chargez une photo pour lancer le Full Identity Scan</div>
              <div className="text-[10px] text-gray-600 max-w-lg mx-auto">
                Le système analyse le visage via un réseau neuronal profond (ArcFace), convertit les caractéristiques biométriques
                (distance inter-oculaire, forme du nez, structure de la mâchoire, texture de la peau) en un vecteur 512D,
                puis recherche cette empreinte numérique à travers des milliards d'images sur Internet via 5 moteurs de recherche
                et 19 réseaux sociaux. Anti-fraude intégré (détection photos, masques, deepfakes).
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Face Analysis Detail Card ───────────────────────────────────────────────

function AnalysisDetailCard({ analysis, title }: { analysis: Record<string, unknown>; title?: string }) {
  const emotions = analysis.emotions as Record<string, number> | undefined;
  const quality = analysis.face_quality as number | undefined;

  return (
    <div className="bg-surface-tertiary rounded-lg border border-border-subtle p-4 space-y-4">
      {title && <h4 className="text-sm font-semibold text-gray-200">{title}</h4>}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="text-xs text-gray-500">Age</span>
          <p className="text-lg font-bold text-gray-200">{String(analysis.age ?? '--')}</p>
        </div>
        <div>
          <span className="text-xs text-gray-500">Genre</span>
          <p className="text-lg font-bold text-gray-200">{String(analysis.gender ?? '--')}</p>
          {analysis.gender_confidence != null && (
            <p className="text-xs text-gray-500">{(Number(analysis.gender_confidence) * 100).toFixed(0)}% confidence</p>
          )}
        </div>
        <div>
          <span className="text-xs text-gray-500">Emotion dominante</span>
          <p className="text-sm font-semibold text-amber-400 capitalize">{String(analysis.dominant_emotion ?? '--')}</p>
        </div>
        <div>
          <span className="text-xs text-gray-500">Direction du regard</span>
          <p className="text-sm font-semibold text-blue-400 capitalize">{String(analysis.gaze_direction ?? '--')}</p>
        </div>
      </div>

      {quality != null && (
        <div>
          <span className="text-xs text-gray-500">Qualite du visage</span>
          <QualityGauge value={quality} />
        </div>
      )}

      {emotions && (
        <div>
          <span className="text-xs text-gray-500 block mb-2">Distribution des emotions</span>
          <EmotionBars emotions={emotions} />
        </div>
      )}
    </div>
  );
}

// ── Detection Section ───────────────────────────────────────────────────────

function DetectSection() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<DetectResponse | null>(null);
  const detect = useDetectFaces();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-200">Face Detection</h3>
        <p className="text-xs text-gray-500 mt-1">
          Detect faces, extract bounding boxes, and analyze attributes (age, gender, emotion, quality, gaze direction).
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <ImageDropZone id="detect" file={file} onFileChange={setFile} />
          <button
            onClick={() => file && detect.mutate(file, { onSuccess: setResult })}
            disabled={!file || detect.isPending}
            className={BTN}
          >
            {detect.isPending ? <><Spinner /> Detecting...</> : 'Detect Faces'}
          </button>
          {detect.isError && <ErrorMsg error={detect.error} />}
        </div>

        <div>
          {result && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span className="font-semibold text-gray-200">{result.faces_detected} face(s) detected</span>
                <span>{formatMs(result.processing_time_ms)}</span>
              </div>
              {result.results.map((face) => (
                <div key={face.face_index} className="space-y-2">
                  <div className="flex items-center justify-between p-2 bg-surface-secondary rounded-lg border border-border-subtle">
                    <span className="text-sm text-gray-200">Face #{face.face_index + 1}</span>
                    <ConfidenceBadge value={face.confidence} />
                  </div>
                  <AnalysisDetailCard analysis={face.analysis} />
                </div>
              ))}
            </div>
          )}
          {!result && !detect.isPending && (
            <div className="h-full min-h-[200px] flex items-center justify-center border border-border-subtle rounded-lg bg-surface-tertiary text-sm text-gray-500">
              Upload an image to detect faces
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Analyze Section ─────────────────────────────────────────────────────────

function AnalyzeSection() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<AnalyzeResponse | null>(null);
  const analyze = useAnalyzeFace();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-200">Analyse faciale approfondie</h3>
        <p className="text-xs text-gray-500 mt-1">
          Analyse detaillee : age, genre, emotions, direction du regard, qualite. Utilise le pipeline AI complet.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <ImageDropZone id="analyze" file={file} onFileChange={setFile} />
          <button
            onClick={() => file && analyze.mutate(file, { onSuccess: setResult })}
            disabled={!file || analyze.isPending}
            className={BTN}
          >
            {analyze.isPending ? <><Spinner /> Analyzing...</> : 'Analyze Face'}
          </button>
          {analyze.isError && <ErrorMsg error={analyze.error} />}
        </div>

        <div className="space-y-3">
          {result && result.analyses.map((a, i) => (
            <AnalysisDetailCard
              key={i}
              title={`Face #${i + 1} — Confidence ${(a.confidence * 100).toFixed(1)}%`}
              analysis={a as unknown as Record<string, unknown>}
            />
          ))}
          {!result && !analyze.isPending && (
            <div className="h-full min-h-[200px] flex items-center justify-center border border-border-subtle rounded-lg bg-surface-tertiary text-sm text-gray-500">
              Upload an image for deep analysis
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Recognize Section ───────────────────────────────────────────────────────

function RecognizeSection() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<RecognizeResponse | null>(null);
  const recognize = useRecognizeFaces();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-200">Face Recognition</h3>
        <p className="text-xs text-gray-500 mt-1">
          Identify faces against enrolled users in the gallery (1:N matching). Shows detection + recognition confidence.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <ImageDropZone id="recognize" file={file} onFileChange={setFile} />
          <button
            onClick={() => file && recognize.mutate(file, { onSuccess: setResult })}
            disabled={!file || recognize.isPending}
            className={BTN}
          >
            {recognize.isPending ? <><Spinner /> Recognizing...</> : 'Recognize Faces'}
          </button>
          {recognize.isError && <ErrorMsg error={recognize.error} />}
        </div>

        <div>
          {result && (
            <div className="space-y-3">
              <div className="text-xs text-gray-400">
                {result.faces_detected} face(s) | {formatMs(result.processing_time_ms)}
              </div>
              {result.results.map((r) => (
                <div key={r.face_index} className="bg-surface-tertiary rounded-lg border border-border-subtle p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`text-lg font-bold ${r.person_name === 'Unknown' ? 'text-amber-400' : 'text-green-400'}`}>
                        {r.person_name}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">Detection: {(r.detection_confidence * 100).toFixed(1)}%</span>
                    </div>
                    {r.recognition_confidence > 0 && <ConfidenceBadge value={r.recognition_confidence} />}
                  </div>
                  {r.recognition_confidence > 0 && (
                    <div>
                      <span className="text-xs text-gray-500">Match Confidence</span>
                      <QualityGauge value={r.recognition_confidence} />
                    </div>
                  )}
                  <AnalysisDetailCard analysis={r.analysis} />
                </div>
              ))}
            </div>
          )}
          {!result && !recognize.isPending && (
            <div className="h-full min-h-[200px] flex items-center justify-center border border-border-subtle rounded-lg bg-surface-tertiary text-sm text-gray-500">
              Upload an image to identify
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Enroll Section ──────────────────────────────────────────────────────────

function EnrollSection() {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [result, setResult] = useState<EnrollResponse | null>(null);
  const enroll = useEnrollFace();

  function handleEnroll() {
    if (!file || !name.trim()) return;
    enroll.mutate(
      { files: [file], name: name.trim() },
      { onSuccess: (r) => { setResult(r); setFile(null); setName(''); } },
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-200">Face Enrollment</h3>
        <p className="text-xs text-gray-500 mt-1">
          Register a person in the biometric gallery. Face encodings are generated via ArcFace and stored in the gallery + SQLite DB.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Person Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter full name..."
              className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-4 py-2.5 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
            />
          </div>
          <ImageDropZone id="enroll" file={file} onFileChange={setFile} label="Drop face image" />
          <button onClick={handleEnroll} disabled={!file || !name.trim() || enroll.isPending} className={BTN}>
            {enroll.isPending ? <><Spinner /> Enrolling...</> : 'Enroll Face'}
          </button>
          {enroll.isError && <ErrorMsg error={enroll.error} />}
        </div>
        <div>
          {result && (
            <div className="p-5 rounded-xl border bg-green-500/5 border-green-500/30 space-y-2">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span className="text-green-400 font-semibold">Enrolled successfully</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-surface-tertiary p-2 rounded"><span className="text-gray-500">Name:</span> <span className="text-gray-200">{result.name}</span></div>
                <div className="bg-surface-tertiary p-2 rounded"><span className="text-gray-500">User ID:</span> <span className="text-gray-200 font-mono">{result.user_id}</span></div>
                <div className="bg-surface-tertiary p-2 rounded"><span className="text-gray-500">Images:</span> <span className="text-gray-200">{result.images_processed}</span></div>
                <div className="bg-surface-tertiary p-2 rounded"><span className="text-gray-500">Encodings:</span> <span className="text-gray-200">{result.encodings_created}</span></div>
              </div>
              <p className="text-xs text-gray-500">Source: ArcFace encoder | Storage: Gallery + SQLite</p>
            </div>
          )}
          {!result && (
            <div className="h-full min-h-[200px] flex items-center justify-center border border-border-subtle rounded-lg bg-surface-tertiary text-sm text-gray-500">
              Enter name and upload a face to enroll
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Compare Section ─────────────────────────────────────────────────────────

function CompareSection() {
  const [fileA, setFileA] = useState<File | null>(null);
  const [fileB, setFileB] = useState<File | null>(null);
  const [result, setResult] = useState<CompareResponse | null>(null);
  const compare = useCompareFaces();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-200">Face Comparison (1:1)</h3>
        <p className="text-xs text-gray-500 mt-1">
          Biometric verification between two face images. Uses cosine similarity on ArcFace embeddings.
          Source: ArcFace model + optional BiometricComparator for advanced multi-metric analysis.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <ImageDropZone id="compare-a" file={fileA} onFileChange={setFileA} label="Face A" />
        <ImageDropZone id="compare-b" file={fileB} onFileChange={setFileB} label="Face B" />
      </div>
      <button
        onClick={() => fileA && fileB && compare.mutate({ fileA, fileB }, { onSuccess: setResult })}
        disabled={!fileA || !fileB || compare.isPending}
        className={BTN}
      >
        {compare.isPending ? <><Spinner /> Comparing...</> : 'Compare Faces'}
      </button>
      {compare.isError && <ErrorMsg error={compare.error} />}

      {result && (
        <div className="space-y-4">
          <div className={`p-6 rounded-xl border text-center ${result.is_match ? 'bg-green-500/5 border-green-500/30' : 'bg-red-500/5 border-red-500/30'}`}>
            <div className={`text-2xl font-bold ${result.is_match ? 'text-green-400' : 'text-red-400'}`}>
              {result.is_match ? 'MATCH — Same Person' : 'NO MATCH — Different People'}
            </div>
            <div className="mt-2">
              <span className="text-3xl font-bold text-gray-200">{(result.similarity * 100).toFixed(1)}%</span>
              <span className="text-sm text-gray-500 ml-2">similarity (threshold: {(result.threshold * 100).toFixed(0)}%)</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="bg-surface-tertiary p-3 rounded-lg border border-border-subtle">
              <span className="text-gray-500">Face A</span>
              <p className={`font-semibold ${result.face_a_detected ? 'text-green-400' : 'text-red-400'}`}>
                {result.face_a_detected ? 'Detected' : 'Not found'}
              </p>
            </div>
            <div className="bg-surface-tertiary p-3 rounded-lg border border-border-subtle">
              <span className="text-gray-500">Face B</span>
              <p className={`font-semibold ${result.face_b_detected ? 'text-green-400' : 'text-red-400'}`}>
                {result.face_b_detected ? 'Detected' : 'Not found'}
              </p>
            </div>
            <div className="bg-surface-tertiary p-3 rounded-lg border border-border-subtle">
              <span className="text-gray-500">Processing</span>
              <p className="font-semibold text-gray-200">{formatMs(result.processing_time_ms)}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Source: ArcFace cosine similarity | Model: 512-dimensional embedding | Biometric comparator: {result.biometric_detail ? 'Available' : 'N/A'}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Search Section ──────────────────────────────────────────────────────────

function SearchSection() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<SearchResponse | null>(null);
  const search = useSearchFace();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-200">Reverse Face Search</h3>
        <p className="text-xs text-gray-500 mt-1">
          Search for similar faces across the FAISS-indexed database. Uses approximate nearest neighbor (ANN) for fast retrieval.
          Source: FAISS index ({'{'}512d vectors{'}'}) + ArcFace encoding.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <ImageDropZone id="search" file={file} onFileChange={setFile} />
          <button
            onClick={() => file && search.mutate(file, { onSuccess: setResult })}
            disabled={!file || search.isPending}
            className={BTN}
          >
            {search.isPending ? <><Spinner /> Searching...</> : 'Search Database'}
          </button>
          {search.isError && <ErrorMsg error={search.error} />}
        </div>

        <div>
          {result && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>{result.matches_found} match(es) found</span>
                <span>{formatMs(result.search_time_ms)}</span>
              </div>
              {result.matches.map((m) => (
                <div key={m.rank} className="bg-surface-tertiary rounded-lg border border-border-subtle p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-helios-700/40 border border-cyber-cyan/20 flex items-center justify-center text-xs font-bold text-cyber-cyan">
                      {m.rank}
                    </span>
                    <div>
                      <span className="text-sm font-medium text-gray-200">{m.person_name}</span>
                      {m.image_path && <span className="ml-2 text-xs text-gray-500">{m.image_path}</span>}
                    </div>
                  </div>
                  <ConfidenceBadge value={m.confidence} />
                </div>
              ))}
              {result.matches_found === 0 && (
                <div className="text-center py-6 text-sm text-gray-500">No matches in FAISS index</div>
              )}
              <p className="text-xs text-gray-500 mt-2">Source: FAISS ANN index | Metric: L2/cosine</p>
            </div>
          )}
          {!result && !search.isPending && (
            <div className="h-full min-h-[200px] flex items-center justify-center border border-border-subtle rounded-lg bg-surface-tertiary text-sm text-gray-500">
              Upload a face to search the database
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── OSINT Analysis Section ──────────────────────────────────────────────────

function OsintSection() {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<OsintAnalyzeResponse | null>(null);
  const osint = useOsintAnalyze();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-200">OSINT Image Analysis</h3>
        <p className="text-xs text-gray-500 mt-1">
          Cross-reference face detection with OSINT intelligence. Extracts faces + image metadata (EXIF, GPS, camera model).
          Interconnects Face AI Lab with the OSINT Image Analyzer module.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <ImageDropZone id="osint" file={file} onFileChange={setFile} />
          <button
            onClick={() => file && osint.mutate(file, { onSuccess: setResult })}
            disabled={!file || osint.isPending}
            className={BTN}
          >
            {osint.isPending ? <><Spinner /> Analyzing...</> : 'OSINT Analyze'}
          </button>
          {osint.isError && <ErrorMsg error={osint.error} />}
        </div>

        <div className="space-y-3">
          {result && (
            <>
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>{result.faces_count} face(s) detected</span>
                <span>{formatMs(result.processing_time_ms)}</span>
              </div>

              {result.faces.map((face, i) => (
                <div key={i} className="bg-surface-tertiary rounded-lg border border-border-subtle p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-200">Face #{i + 1}</span>
                    <div className="flex items-center gap-2">
                      <ConfidenceBadge value={face.confidence} />
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${face.has_encoding ? 'bg-green-500/15 text-green-400 border-green-500/30' : 'bg-gray-500/15 text-gray-400 border-gray-500/30'}`}>
                        {face.has_encoding ? 'Encoded' : 'No encoding'}
                      </span>
                    </div>
                  </div>
                  {face.analysis && <AnalysisDetailCard analysis={face.analysis} />}
                </div>
              ))}

              {Object.keys(result.osint_metadata).length > 0 && (
                <div className="bg-surface-tertiary rounded-lg border border-amber-500/30 p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-amber-400">OSINT Metadata</h4>
                  <div className="space-y-1">
                    {Object.entries(result.osint_metadata).map(([key, val]) => (
                      <div key={key} className="flex justify-between text-xs">
                        <span className="text-gray-400">{key}</span>
                        <span className="text-gray-200 font-mono max-w-[250px] truncate">{String(val)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Source: OSINT Image Analyzer (EXIF, metadata extraction)</p>
                </div>
              )}

              {Object.keys(result.osint_metadata).length === 0 && (
                <div className="p-3 bg-surface-tertiary rounded-lg border border-border-subtle text-xs text-gray-500 text-center">
                  No OSINT metadata extracted (OSINT Image Analyzer module may not be available)
                </div>
              )}
            </>
          )}
          {!result && !osint.isPending && (
            <div className="h-full min-h-[200px] flex items-center justify-center border border-border-subtle rounded-lg bg-surface-tertiary text-sm text-gray-500">
              Upload an image for OSINT cross-analysis
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── History Section ─────────────────────────────────────────────────────────

function HistorySection() {
  const { data: history, isLoading } = useFaceAIHistory(100);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-200">Recognition History</h3>
        <p className="text-xs text-gray-500 mt-1">
          Log of all face recognition events. Source: SQLite biometric database.
        </p>
      </div>

      {history?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Total Recognitions" value={history.stats.total_recognitions} color="purple" />
          <KPI label="Today" value={history.stats.today} color="cyan" />
          <KPI label="Avg Confidence" value={`${(history.stats.avg_confidence * 100).toFixed(0)}%`} color="green" />
          <KPI label="Top Person" value={history.stats.top_person?.recognized_name ?? 'N/A'} color="amber" />
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-8"><Spinner className="w-6 h-6" /></div>
      )}

      {history?.records && history.records.length > 0 && (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {history.records.map((r) => {
            let analysis: Record<string, unknown> = {};
            try { analysis = JSON.parse(r.analysis_json); } catch { /* ignore */ }

            return (
              <div key={r.id} className="bg-surface-tertiary rounded-lg border border-border-subtle p-3 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-helios-700/40 border border-cyber-cyan/20 flex items-center justify-center text-xs font-bold text-cyber-cyan flex-shrink-0">
                  {r.recognized_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${r.recognized_name === 'Unknown' ? 'text-amber-400' : 'text-green-400'}`}>
                      {r.recognized_name}
                    </span>
                    {r.confidence > 0 && <ConfidenceBadge value={r.confidence} />}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                    <span>{r.image_path}</span>
                    <span>Source: {r.source}</span>
                    {!!analysis.age && <span>Age: {String(analysis.age)}</span>}
                    {!!analysis.dominant_emotion && <span className="capitalize">{String(analysis.dominant_emotion)}</span>}
                  </div>
                </div>
                <div className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                  {new Date(r.timestamp).toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {history?.records?.length === 0 && !isLoading && (
        <div className="text-center py-8 text-sm text-gray-500">No recognition history yet</div>
      )}
    </div>
  );
}

// ── Gallery Section ─────────────────────────────────────────────────────────

function GallerySection() {
  const { data: usersData, isLoading } = useFaceAIUsers();
  const deleteUser = useDeleteFaceAIUser();

  const users = usersData?.users ?? [];
  const galleryNames = usersData?.gallery_names ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-200">Identity Gallery</h3>
          <p className="text-xs text-gray-500 mt-1">
            {usersData?.total ?? 0} registered identities | Gallery: {galleryNames.length} names.
            Source: SQLite DB + In-memory ArcFace gallery.
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-8"><Spinner className="w-6 h-6" /></div>
      )}

      {users.length > 0 ? (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {users.map((user, idx) => (
            <div
              key={String(user.id ?? idx)}
              className="bg-surface-tertiary rounded-lg border border-border-subtle p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-helios-700/40 border border-cyber-cyan/20 flex items-center justify-center text-sm font-bold text-cyber-cyan">
                  {String(user.name ?? '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-200">{String(user.name ?? 'Unknown')}</div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                    <span>ID: {String(user.id ?? '--')}</span>
                    <span>Images: {String(user.num_images ?? 0)}</span>
                    <span>Created: {user.created_at ? new Date(String(user.created_at)).toLocaleDateString() : '--'}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  if (confirm(`Delete "${user.name}"?`)) {
                    deleteUser.mutate(Number(user.id));
                  }
                }}
                disabled={deleteUser.isPending}
                className="px-3 py-1.5 text-xs text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/10 transition-colors disabled:opacity-50"
              >
                {deleteUser.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          ))}
        </div>
      ) : galleryNames.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 mb-2">Gallery names (in-memory only):</p>
          {galleryNames.map((name) => (
            <div key={name} className="bg-surface-tertiary rounded-lg border border-border-subtle p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-helios-700/40 border border-cyber-cyan/20 flex items-center justify-center text-xs font-bold text-cyber-cyan">
                {name.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-medium text-gray-200">{name}</span>
            </div>
          ))}
        </div>
      ) : !isLoading ? (
        <div className="text-center py-8 text-sm text-gray-500">No enrolled users. Use the Enrollment tab to register faces.</div>
      ) : null}

      <p className="text-xs text-gray-500">
        Sources: SQLite biometric DB ({String(users.length)} records) | In-memory gallery ({galleryNames.length} persons) | FAISS index for ANN search
      </p>
    </div>
  );
}
